/**
 * Where this app and its sibling services live.
 *
 * Vite inlines `import.meta.env` at build time, so these values are frozen
 * into the shipped bundle — there is no runtime env var to change afterwards.
 * Every cross-app link and the API base therefore read from here, and here
 * only, so a domain move is one file plus a rebuild rather than a hunt
 * through components.
 *
 * **An empty string counts as absent.** That is not a nicety: a Dockerfile
 * that declares `ARG VITE_API_BASE_URL` and then `ENV VITE_API_BASE_URL=$VITE_API_BASE_URL`
 * sets the variable to `''` when the build arg is not supplied; dotenv then
 * skips the key in `.env.production` because it is already present in the
 * environment, and `??` does not fall back on an empty string. That exact
 * chain once shipped every SPA pointing at nowhere. `read()` below treats
 * empty as unset, so a missing build arg lands on the production default
 * instead of the void.
 *
 * Dev keeps the localhost defaults so `pnpm dev` never talks to production.
 */

/** Production defaults — the branded domains, used when no env var is supplied. */
const PROD_FALLBACK = {
  api: 'https://api.terrashoppy.com/api/v1',
  storefront: 'https://terrashoppy.com',
  vendor: 'https://vendor.terrashoppy.com',
  admin: 'https://app.terrashoppy.com',
} as const;

/** What `pnpm dev` uses, so a local build never reaches the live platform. */
const DEV_FALLBACK = {
  api: 'http://localhost:4000/api/v1',
  storefront: 'http://localhost:5173',
  vendor: 'http://localhost:5175',
  admin: 'http://localhost:5174',
} as const;

function read(value: string | undefined, key: keyof typeof PROD_FALLBACK): string {
  const trimmed = value?.trim();
  const raw =
    trimmed && trimmed.length > 0
      ? trimmed
      : import.meta.env.DEV
        ? DEV_FALLBACK[key]
        : PROD_FALLBACK[key];
  // No trailing slash: every call site appends a path that starts with one.
  return raw.replace(/\/+$/, '');
}

/**
 * API origin **including** the `/api/v1` prefix — the shape the existing env
 * var has always had. OAuth callback URLs are built from it, because the
 * callback is served by the API, not by the storefront.
 */
export const API_BASE_URL = read(import.meta.env.VITE_API_BASE_URL, 'api');

/** The shop the Live Preview panel loads in its frame. */
export const STOREFRONT_URL = read(import.meta.env.VITE_STOREFRONT_URL, 'storefront');
